// ─────────────────────────────────────────────────────────────────────────────
//  FilterService  (pure — no Lit dependency)
//  ────────────────────────────────────────────────────────────────────────────
//  Contains all filter matching logic as static methods.
//  Can be used standalone in any framework or unit test:
//
//    const visible = FilterService.filteredData(rows, filters, globalValue, globalFields);
//    const passes  = FilterService.match(row['name'], 'alice', 'startsWith');
//
//  ─────────────────────────────────────────────────────────────────────────────
//  FilterController  (Lit Reactive Controller)
//  ────────────────────────────────────────────────────────────────────────────
//  Wraps FilterService with reactive state and Lit host integration.
//  Delegates all matching to FilterService.
//
//  Usage inside UwcDatatable:
//
//    private _filter = new FilterController(this);
//
//    override willUpdate(changed: PropertyValues) {
//      if (changed.has('filters') && this.filters) {
//        this._filter.filters = { ...this.filters };
//      }
//      if (changed.has('globalFilterFields')) {
//        this._filter.globalFilterFields = this.globalFilterFields ?? [];
//      }
//      if (changed.has('globalFilterValue') && this.globalFilterValue !== undefined) {
//        this._filter.globalFilterValue = this.globalFilterValue;
//      }
//    }
//
//    const allRows = this._filter.filteredData(this.value ?? []);
//
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactiveController, ReactiveControllerHost } from 'lit';

// ── Public types ──────────────────────────────────────────────────────────────

/** Match strategy applied when comparing a cell value to a filter value. */
export type FilterMatchMode =
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'equals'
  | 'notEquals'
  | 'in'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte';

/** Human-readable labels for each match mode — useful for UI menus. */
export const FILTER_MODE_LABELS: Record<FilterMatchMode, string> = {
  contains:    'Contains',
  notContains: 'Not Contains',
  startsWith:  'Starts with',
  endsWith:    'Ends with',
  equals:      'Equals',
  notEquals:   'Not equals',
  in:          'In',
  lt:          'Less than',
  lte:         'Less than or equal',
  gt:          'Greater than',
  gte:         'Greater than or equal',
};

/** A single column's active filter entry. */
export interface FilterMetaData {
  value: string | number | boolean | string[] | null | undefined;
  matchMode: FilterMatchMode;
}

/** Map of field → filter entry. Pass as the `filters` property of uwc-datatable. */
export type DataTableFilterMeta = Record<string, FilterMetaData>;

/**
 * A single option for built-in multiselect / dropdown filter panels.
 * Pass a plain string when the display label equals the stored value,
 * or `{ label, value }` when they differ (e.g. localised display names).
 */
export type FilterMenuItem = string | { label: string; value: unknown };

/** Normalise a FilterMenuItem to a `{ label, value }` pair for rendering. */
export function resolveFilterMenuItem(item: FilterMenuItem): { label: string; value: unknown } {
  return typeof item === 'string' ? { label: item, value: item } : item;
}

/** Detail payload of the `uwc-filter` event. */
export interface FilterChangeDetail {
  filters: DataTableFilterMeta;
  globalFilterValue: string;
}

/** Options passed to a custom `filterElement` renderer. */
export interface FilterElementOptions {
  field: string;
  filterValue: unknown;
  matchMode: FilterMatchMode;
  filterCallback: (value: unknown, matchMode?: FilterMatchMode) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterService — pure, no Lit dependency
// ─────────────────────────────────────────────────────────────────────────────

export class FilterService {
  /**
   * Test whether a single cell value satisfies the given filter criteria.
   *
   * @param cellVal    - The raw value from the data row.
   * @param filterVal  - The value entered by the user (string, number, or string[] for 'in' mode).
   * @param mode       - One of the FilterMatchMode strategies.
   * @returns true if the cell passes the filter.
   */
  static match(
    cellVal: unknown,
    filterVal: string | number | boolean | string[] | null | undefined,
    mode: FilterMatchMode,
  ): boolean {
    // 'in' mode: filterVal is an array of allowed values
    if (mode === 'in') {
      if (!Array.isArray(filterVal) || filterVal.length === 0) return true;
      const cellStr = String(cellVal ?? '').toLowerCase();
      return filterVal.some(v => cellStr === String(v).toLowerCase());
    }

    if (cellVal == null || filterVal == null) return false;

    const cell    = String(cellVal).toLowerCase();
    const filt    = String(filterVal).toLowerCase();
    const cellNum = Number(cellVal);
    const filtNum = Number(filterVal);

    switch (mode) {
      case 'contains':    return cell.includes(filt);
      case 'notContains': return !cell.includes(filt);
      case 'startsWith':  return cell.startsWith(filt);
      case 'endsWith':    return cell.endsWith(filt);
      case 'equals':      return cell === filt;
      case 'notEquals':   return cell !== filt;
      case 'lt':          return !isNaN(cellNum) && !isNaN(filtNum) && cellNum <  filtNum;
      case 'lte':         return !isNaN(cellNum) && !isNaN(filtNum) && cellNum <= filtNum;
      case 'gt':          return !isNaN(cellNum) && !isNaN(filtNum) && cellNum >  filtNum;
      case 'gte':         return !isNaN(cellNum) && !isNaN(filtNum) && cellNum >= filtNum;
      default:            return cell.includes(filt);
    }
  }

  /**
   * Return a filtered copy of `data`.
   *
   * Column filters are applied first (AND logic — all active filters must pass).
   * Global filter is applied second (OR logic — any of globalFilterFields must match).
   *
   * Does not mutate the original array.
   */
  static filteredData(
    data: Record<string, unknown>[],
    filters: DataTableFilterMeta,
    globalFilterValue = '',
    globalFilterFields: string[] = [],
  ): Record<string, unknown>[] {
    let result = data;

    // ── Column filters ───────────────────────────────────────────────────────
    const activeFields = Object.keys(filters).filter(f =>
      FilterService.isActive(filters[f]),
    );
    if (activeFields.length > 0) {
      result = result.filter(row =>
        activeFields.every(field => {
          const meta = filters[field];
          return FilterService.match(
            FilterService.resolve(row, field),
            meta.value,
            meta.matchMode,
          );
        }),
      );
    }

    // ── Global filter ────────────────────────────────────────────────────────
    if (globalFilterValue.trim() && globalFilterFields.length > 0) {
      const q = globalFilterValue.toLowerCase();
      result = result.filter(row =>
        globalFilterFields.some(field =>
          String(FilterService.resolve(row, field) ?? '').toLowerCase().includes(q),
        ),
      );
    }

    return result;
  }

  /** Whether a FilterMetaData entry has a non-empty value. */
  static isActive(meta: FilterMetaData | undefined): boolean {
    if (!meta) return false;
    if (Array.isArray(meta.value)) return meta.value.length > 0;
    return meta.value !== '' && meta.value != null;
  }

  /** Traverse dot-notation paths: "address.city" → nested value. */
  static resolve(row: Record<string, unknown>, field: string): unknown {
    return field.split('.').reduce<unknown>((acc, key) => {
      if (acc !== null && acc !== undefined && typeof acc === 'object') {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, row);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterController — Lit Reactive Controller
// ─────────────────────────────────────────────────────────────────────────────

export class FilterController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & EventTarget;

  // ── Mutable filter state (single source of truth) ─────────────────────────

  filters: DataTableFilterMeta = {};
  globalFilterValue            = '';
  globalFilterFields: string[] = [];

  constructor(host: ReactiveControllerHost & EventTarget) {
    this._host = host;
    host.addController(this);
  }

  hostConnected()    {}
  hostDisconnected() {}

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Set or clear a column filter.
   * When value is empty but matchMode is given, the entry is kept (with empty
   * value) so the chosen mode survives re-renders and is used on the next keystroke.
   * Omitting both value and matchMode removes the field's entry entirely.
   */
  updateFilter(
    field: string,
    value: unknown,
    matchMode?: FilterMatchMode,
  ): void {
    const existing     = this.filters[field];
    const resolvedMode: FilterMatchMode = matchMode ?? existing?.matchMode ?? 'contains';

    const isEmpty =
      value === '' ||
      value == null ||
      (Array.isArray(value) && value.length === 0);

    if (isEmpty && matchMode === undefined) {
      // No value, no explicit mode change — remove the entry entirely
      const next = { ...this.filters };
      delete next[field];
      this.filters = next;
    } else {
      // Preserve matchMode even when value is empty so it survives re-renders
      this.filters = {
        ...this.filters,
        [field]: {
          value: isEmpty ? '' : value as string | number | boolean | string[],
          matchMode: resolvedMode,
        },
      };
    }
    this._host.requestUpdate();
    this._emit();
  }

  /** Update the global search string. */
  updateGlobal(value: string): void {
    this.globalFilterValue = value;
    this._host.requestUpdate();
    this._emit();
  }

  /** Remove a single column filter. */
  clearFilter(field: string): void {
    const next = { ...this.filters };
    delete next[field];
    this.filters = next;
    this._host.requestUpdate();
    this._emit();
  }

  /** Remove all column filters and reset the global search string. */
  clearAll(): void {
    this.filters           = {};
    this.globalFilterValue = '';
    this._host.requestUpdate();
    this._emit();
  }

  /** Whether a column currently has a non-empty filter value. */
  isActive(field: string): boolean {
    return FilterService.isActive(this.filters[field]);
  }

  /** Whether any filter is currently active (column or global). */
  hasActiveFilters(): boolean {
    return (
      Object.keys(this.filters).some(f => this.isActive(f)) ||
      this.globalFilterValue.trim().length > 0
    );
  }

  /**
   * Return a filtered copy of `data` by delegating to FilterService.
   * Does not mutate the original array.
   */
  filteredData(data: Record<string, unknown>[]): Record<string, unknown>[] {
    return FilterService.filteredData(
      data,
      this.filters,
      this.globalFilterValue,
      this.globalFilterFields,
    );
  }

  // ── Event ──────────────────────────────────────────────────────────────────

  private _emit(): void {
    this._host.dispatchEvent(
      new CustomEvent<FilterChangeDetail>('uwc-filter', {
        bubbles: true,
        composed: true,
        detail: {
          filters:           { ...this.filters },
          globalFilterValue: this.globalFilterValue,
        },
      }),
    );
  }
}
